import React, { memo } from "react";

import {
    Box,
    Button,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

const StepContainer = ({
    children,
    handleSubmit = () => {},
    onSubmit = () => {},
    isDirty,
    renderButtons = null,
    isLoading = false,
    noMarginTop,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();

    return (
        <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                height: { xs: "auto", md: "100%" },
                width: "100%",
                flex: 1,
                minWidth: 0,
                minHeight: { xs: "100dvh", md: 0 },
            }}
        >
            {/* Scrollable children area */}
            <Box
                sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: { xs: "visible", md: "auto" },
                    p: { xs: 1.5, sm: 2, md: 3, lg: 4 },
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 1,
                    minWidth: 0,
                }}
            >
                <Stack
                    gap={{ xs: 3, sm: 4, md: 4 }}
                    sx={{
                        width: "100%",
                        maxWidth: { xs: "100%", sm: 800, md: 1000 },
                        maxHeight: "100%",
                        minHeight: "fit-content",
                        minWidth: 0,
                        mt: {
                            xs: 1,
                            sm: 2,
                            md: noMarginTop ? 0 : 4,
                            lg: noMarginTop ? 0 : 8,
                        },
                        zIndex: 1,
                    }}
                >
                    {children}
                </Stack>
            </Box>

            {/* Sticky footer - Enhanced for mobile */}
            <Box
                sx={{
                    borderTop: "1px solid #eee",
                    p: { xs: 1.5, sm: 2, md: 2 },
                    px: { xs: 2, sm: 3, md: 4 },
                    maxWidth: { xs: "100%", sm: 800, md: 1000 },
                    width: "100%",
                    mx: "auto",
                    backgroundColor: "#fff",
                    position: { xs: "sticky", md: "relative" },
                    bottom: 0,
                    boxShadow: {
                        xs: "0 -2px 10px rgba(0,0,0,0.1)",
                        md: "none",
                    },
                }}
                style={{
                    zIndex: 9999999,
                }}
            >
                {!renderButtons && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: {
                                xs: "flex-end",
                                md: "space-between",
                            },
                            alignItems: { xs: "stretch", sm: "center" },
                            gap: { xs: 2, sm: 0 },
                            width: "100%",
                        }}
                    >
                        <Typography
                            color="text.secondary"
                            variant="body2"
                            sx={{
                                textAlign: { xs: "center", sm: "left" },
                                fontSize: { xs: "14px", sm: "16px" },
                            }}
                        >
                            {isDirty && "There are unsaved changes."}
                        </Typography>

                        {/* Mobile: Both buttons side by side */}
                        {isMobile ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    width: "100%",
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => navigate("/landing-pages")}
                                    sx={{
                                        height: "40px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        flex: 1,
                                    }}
                                    style={{
                                        minWidth: "unset !important",
                                    }}
                                >
                                    EXIT WIZARD
                                </Button>
                                <div
                                    className="position-relative overflow-hidden z-1"
                                    style={{
                                        flex: 1,
                                    }}
                                >
                                    {isLoading && (
                                        <div className="circle-loader">
                                            <ClipLoader
                                                color={"#000"}
                                                size={17}
                                            />
                                        </div>
                                    )}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        style={{
                                            minWidth: "unset !important",
                                        }}
                                        disabled={isLoading}
                                        sx={{
                                            height: "40px",
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            width: "100%",
                                        }}
                                    >
                                        SAVE & CONTINUE
                                    </Button>
                                </div>
                            </Box>
                        ) : (
                            /* Desktop: Only save button */
                            <div
                                className="position-relative overflow-hidden z-1"
                                style={{
                                    marginLeft: "auto",
                                    width: "auto",
                                }}
                            >
                                {isLoading && (
                                    <div className="circle-loader">
                                        <ClipLoader color={"#000"} size={17} />
                                    </div>
                                )}
                                <Box sx={{ width: "auto" }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={isLoading}
                                        sx={{
                                            height: "fit-content",
                                            width: "auto",
                                            fontSize: "16px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        SAVE & CONTINUE
                                    </Button>
                                </Box>
                            </div>
                        )}
                    </Box>
                )}
                {renderButtons && renderButtons()}
            </Box>
        </Box>
    );
};

export default memo(StepContainer);
